// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// What the 3G shutdown did to individual towers. This tracks entity identity
// through time rather than totals: "Telstra's 3G count went 9,081 to 0" is one
// number, but underneath it thousands of towers moved in different directions,
// and some lost long reach and got nothing back.
//
// Carrier is single-select on purpose — the three shut 3G down in different
// years and overlaying them would state something false.
//
// The layout is pure deterministic geometry solved once per render. Nothing
// relaxes, settles or animates into position.

import type { TowerData, Carrier, FlowKind } from '../types';
import { CARRIER_ALIAS, CARRIER_COLOR, FLOW_COLOR, FLOW_LABEL } from '../colors';
import { attr, esc, num, pct } from '../format';
import { gloss } from '../glossary';
import { legend, viewHead, onActivate, setPressed } from './ui';
import { ribbonPath, stackColumn } from '../layout';

const TERMINAL_W = 16;

export function mount(el: HTMLElement, data: TowerData): void {
  const { meta, switchoff } = data;
  let carrier: Carrier = 'Telstra';
  let focus: { t: number; from: string; to: string } | null = null;

  el.innerHTML = `
    ${viewHead({
      title: 'What the shutdown took',
      question: 'At an individual tower, was 3G replaced in place — or did the tower quietly get shorter?',
      blurb: `Every ribbon is a group of real towers, matched year to year by their ${gloss('rfnsa', 'site number')}. A tower is described by its <em>reach</em>: whether it carries ${gloss('lowband', 'long-reach spectrum')}, and on which generation. Amber ribbons are the finding — towers that held long-reach 3G and ended up with no long-reach spectrum at all.`,
    })}
    <div class="ctl-row">
      <div class="seg" role="group" aria-label="Carrier">
        ${meta.carriers.map((c) => `<button data-carrier="${c}" class="${c === carrier ? 'active' : ''}" aria-pressed="${c === carrier}">${esc(CARRIER_ALIAS[c])}</button>`).join('')}
      </div>
      <p class="ctl-note">Per-tower tracking starts in ${meta.identityFrom} — the ${meta.years[0]} and ${meta.years[0] + 1} files carry no site number.</p>
    </div>
    <div data-flow></div>
    ${legend((['gain', 'same', 'lost', 'new', 'gone'] as FlowKind[]).map((k) => ({ color: FLOW_COLOR[k], label: FLOW_LABEL[k] })))}
    <div data-cohort></div>
  `;

  const host = el.querySelector('[data-flow]') as HTMLElement;
  const cohortHost = el.querySelector('[data-cohort]') as HTMLElement;

  function render(): void {
    host.innerHTML = alluvial(data, carrier, focus);
    host.querySelectorAll('[data-flow-key]').forEach((r) => {
      onActivate(r, () => {
        const [t, from, to] = r.getAttribute('data-flow-key')!.split('|');
        const same = focus && focus.t === Number(t) && focus.from === from && focus.to === to;
        focus = same ? null : { t: Number(t), from, to };
        render();
      });
    });
    renderCohort();
  }

  function renderCohort(): void {
    if (!focus) {
      const lost = switchoff.carriers[carrier].links.filter((l) => l.kind === 'lost');
      const total = lost.reduce((n, l) => n + l.count, 0);
      cohortHost.innerHTML = `<div class="cohort cohort-idle">
        <p>Click any ribbon to see exactly what that group of towers did.</p>
        <p class="muted">${CARRIER_ALIAS[carrier]}: ${num(total)} tower-transitions lost reach across the tracked years. ${num(meta.totals.reachRegressions)} towers nationally ended up with no long-reach spectrum after carrying long-reach 3G.</p>
      </div>`;
      return;
    }
    const link = switchoff.carriers[carrier].links.find(
      (l) => l.t === focus!.t && l.from === focus!.from && l.to === focus!.to,
    );
    if (!link) {
      cohortHost.innerHTML = '';
      return;
    }
    const yearFrom = switchoff.years[link.t];
    const yearTo = switchoff.years[link.t + 1];
    const originTotal = switchoff.carriers[carrier].links
      .filter((l) => l.t === link.t && l.from === link.from)
      .reduce((n, l) => n + l.count, 0);
    cohortHost.innerHTML = `<div class="cohort cohort-${link.kind}">
      <h3>${num(link.count)} ${CARRIER_ALIAS[carrier]} towers</h3>
      <p><strong>${yearFrom}:</strong> ${esc(stateLabel(data, link.from))} &nbsp;→&nbsp; <strong>${yearTo}:</strong> ${esc(stateLabel(data, link.to))}</p>
      <p class="muted">${pct(link.count / Math.max(originTotal, 1))} of the towers that were "${esc(stateLabel(data, link.from)).toLowerCase()}" in ${yearFrom}. ${describe(link.kind)}</p>
      <button class="btn btn-quiet" data-clear>Clear selection</button>
    </div>`;
    cohortHost.querySelector('[data-clear]')?.addEventListener('click', () => {
      focus = null;
      render();
    });
  }

  el.querySelectorAll('[data-carrier]').forEach((b) => {
    b.addEventListener('click', () => {
      carrier = b.getAttribute('data-carrier') as Carrier;
      focus = null;
      setPressed(el.querySelectorAll('[data-carrier]'), (o) => o === b);
      render();
    });
  });

  render();
}

function stateLabel(data: TowerData, id: string): string {
  if (id === 'new') return 'Not yet in the register';
  if (id === 'gone') return 'Gone from the register';
  return data.switchoff.states.find((s) => s.id === id)?.label ?? id;
}

function describe(kind: FlowKind): string {
  switch (kind) {
    case 'lost':
      return 'These towers are still standing, but they no longer carry spectrum that reaches as far as what they lost.';
    case 'gain':
      return 'These towers came out of the transition with more reach or a newer generation than they went in with.';
    case 'gone':
      return 'These site numbers stopped appearing in the carrier\'s return — decommissioned, re-registered, or reported differently.';
    case 'new':
      return 'These site numbers appear for the first time this year.';
    default:
      return 'These towers carry the same class of spectrum as the year before.';
  }
}

function alluvial(data: TowerData, carrier: Carrier, focus: { t: number; from: string; to: string } | null): string {
  const { switchoff } = data;
  const years = switchoff.years;
  const cols = years.length;
  // Plain strings: link endpoints also carry the 'new' and 'gone' terminals.
  const order: string[] = switchoff.states.map((s) => s.id);
  const nodes = switchoff.carriers[carrier].nodes;
  const links = switchoff.carriers[carrier].links;

  const W = 1080;
  const H = 470;
  // The left pad exists to hold the reach-state names, which are long by
  // design ("3G + modern long-reach"). Without it they are clipped off-canvas.
  const P = { t: 42, b: 30, l: 152, r: 10 };
  const ih = H - P.t - P.b;
  const colW = (W - P.l - P.r) / cols;
  const nodeW = 15;
  const gap = 7;

  const colTotal = (t: number) => order.reduce((n, s) => n + (nodes[t][s] || 0), 0);
  const maxTotal = Math.max(...years.map((_, t) => colTotal(t)));
  const scale = (v: number) => (v / maxTotal) * (ih - gap * (order.length - 1));

  // Node boxes, stacked in a fixed order so a state never moves between years.
  const box: Record<string, { x: number; y: number; h: number }>[] = years.map((_, t) => {
    const boxes = stackColumn(order.map((s) => nodes[t][s] || 0), { top: P.t, scale, gap });
    const out: Record<string, { x: number; y: number; h: number }> = {};
    order.forEach((s, i) => {
      out[s] = { x: P.l + t * colW + colW / 2 - nodeW / 2, y: boxes[i].y, h: boxes[i].h };
    });
    return out;
  });

  // Ribbons leave a node ordered by target, and arrive ordered by source, so
  // they cross as little as possible while staying deterministic.
  const outCursor: Record<string, number>[] = years.map(() => ({}));
  const inCursor: Record<string, number>[] = years.map(() => ({}));

  const ordered = [...links].sort(
    (a, b) => a.t - b.t || order.indexOf(a.from) - order.indexOf(b.from) || order.indexOf(a.to) - order.indexOf(b.to),
  );

  const ribbons = ordered
    .map((l) => {
      const t = l.t;
      const src = l.from === 'new' ? null : box[t][l.from];
      const dst = l.to === 'gone' ? null : box[t + 1][l.to];
      const h = scale(l.count);
      if (h < 0.12) return '';

      let y0: number;
      let x0: number;
      if (src) {
        outCursor[t][l.from] = outCursor[t][l.from] ?? 0;
        y0 = src.y + outCursor[t][l.from];
        outCursor[t][l.from] += h;
        x0 = src.x + nodeW;
      } else {
        // "New to the register" enters from a stub on the left of its column.
        const target = box[t + 1][l.to];
        y0 = target.y + (inCursor[t + 1][l.to] ?? 0);
        x0 = P.l + (t + 1) * colW + colW / 2 - nodeW / 2 - TERMINAL_W;
      }

      let y1: number;
      let x1: number;
      if (dst) {
        inCursor[t + 1][l.to] = inCursor[t + 1][l.to] ?? 0;
        y1 = dst.y + inCursor[t + 1][l.to];
        inCursor[t + 1][l.to] += h;
        x1 = dst.x;
      } else {
        // "Gone" exits to a stub on the right of the year it left.
        y1 = y0;
        x1 = box[t][l.from].x + nodeW + TERMINAL_W;
      }

      const d = ribbonPath(x0, y0, x1, y1, h);

      const key = `${l.t}|${l.from}|${l.to}`;
      const isFocus = focus && focus.t === l.t && focus.from === l.from && focus.to === l.to;
      const dim = focus && !isFocus;
      const tip = [
        `${years[t]} → ${years[t + 1]}`,
        `${stateLabel(data, l.from)} → ${stateLabel(data, l.to)}`,
        `${num(l.count)} ${CARRIER_ALIAS[carrier]} towers`,
        FLOW_LABEL[l.kind],
        'Click to select this group',
      ].join('\n');

      return `<path d="${d}" fill="${FLOW_COLOR[l.kind]}" fill-opacity="${dim ? 0.08 : l.kind === 'same' ? 0.45 : 0.7}"
        class="ribbon${isFocus ? ' ribbon-focus' : ''}" data-flow-key="${attr(key)}" role="button" tabindex="0"
        data-tip="${attr(tip)}" />`;
    })
    .join('');

  const nodeRects = years
    .flatMap((_, t) =>
      order.map((s) => {
        const b = box[t][s];
        const count = nodes[t][s] || 0;
        if (b.h < 0.4) return '';
        return `<rect x="${b.x.toFixed(1)}" y="${b.y.toFixed(1)}" width="${nodeW}" height="${b.h.toFixed(1)}" rx="2"
          fill="var(--text-secondary)" data-tip="${attr(`${years[t]}\n${stateLabel(data, s)}\n${num(count)} towers`)}" />`;
      }),
    )
    .join('');

  const yearLabels = years
    .map(
      (y, t) =>
        `<text x="${(P.l + t * colW + colW / 2).toFixed(1)}" y="26" class="col-year" text-anchor="middle">${y}</text>`,
    )
    .join('');

  // Every state gets a name, including the ones that are a sliver in 2020 —
  // an unlabelled band is unreadable, and the small ones are the interesting
  // ones. Slivers are nudged apart so their labels do not sit on top of
  // each other.
  let lastLabelY = -Infinity;
  const stateKey = order
    .map((s, i) => {
      const b = box[0][s];
      const y = Math.max(b.y + b.h / 2 + 3, lastLabelY + 13);
      lastLabelY = y;
      return `<text x="${P.l - 12}" y="${y.toFixed(1)}" class="state-lab" text-anchor="end">${esc(switchoff.states[i].label)}</text>`;
    })
    .join('');

  return `<div class="chart flow-wrap">
    <svg viewBox="0 0 ${W} ${H}" class="alluvial" role="img"
      aria-label="How ${CARRIER_ALIAS[carrier]} towers changed reach state between ${years[0]} and ${years[years.length - 1]}"
      style="--carrier:${CARRIER_COLOR[carrier]}">
      ${yearLabels}${ribbons}${nodeRects}${stateKey}
    </svg>
  </div>`;
}
